<template>
  <RouterView />
  <Clippy />
</template>
<script lang="ts">
import {
  initialize as initializeNotifications,
  NotificationButton,
} from '@robovision/notifications'
import {
  initialize as initializeRvaiBase,
  useDarkMode,
} from '@robovision/rvai-base'
import { initialize as initializeParams } from '@robovision/rvai-params'
import {
  DaemonParam,
  initialize as initializeDaemonParam,
} from '@robovision/rvai-params-daemon'
import { storeToRefs } from 'pinia'
import {
  Dialog,
  LocalStorage,
  useQuasar,
} from 'quasar'
import {
  computed,
  defineComponent,
  markRaw,
  watch,
} from 'vue'
import { RouterView } from 'vue-router'
import Clippy from '~components/Clippy/Clippy.vue'
import LicenseExpiredDialog from '~components/dialogs/LicenseExpiredDialog.vue'
import NavigationBar from '~components/NavigationBar.vue'
import { useDocs } from '~composables/useDocs'
import { LocalStorageKeys } from '~domain/localStorageKeys'
import type { Member } from '~domain/Member'
import { notificationActionsHandler } from '~notifications/services/notifications'
import { BACK_BUTTON_LABELS } from '~router/navigation/BackButtonLabels'
import { useMembersSidebars } from '~sidebars/composables/useMembersSidebars'
import { useLicensing } from '~stores/composables/useLicensing'
import { useProjectsStore } from '~stores/projectsStore/useProjectsStore'
import { useBrandingStore } from '~stores/useBrandingStore'
import { useExportsStore } from '~stores/useExportsStore'
import { useImportsStore } from '~stores/useImportsStore'
import { useSessionStore } from '~stores/useSessionStore'
import { iconMapFn } from '~utils/iconMapFn'


export default defineComponent({
  name: 'App',

  components: {
    Clippy,
    RouterView,
  },

  setup () {
    const $q = useQuasar()
    const { isDark } = useDarkMode()
    const { helpListener } = useDocs()
    const { shouldDisplayLicenseDialog } = useLicensing()
    const { openEditMemberSidebar } = useMembersSidebars()
    const {
      getLogoUri,
      getPrimaryColor,
      setCustomPrimaryColor,
    } = useBrandingStore()
    const { loadProjects } = useProjectsStore()
    const { loadUnfinishedExports } = useExportsStore()
    const { loadUnfinishedImports } = useImportsStore()
    const sessionStore = useSessionStore()
    const { logout } = sessionStore
    const { currentUser } = storeToRefs(sessionStore)
    const showPopupNotifications = LocalStorage.getItem(LocalStorageKeys.ShowPopupNotifications)

    initializeRvaiBase({
      navigation: {
        backButtonLabels: BACK_BUTTON_LABELS,
        component: markRaw(NavigationBar) as never,
        delayOnSidebarToggle: 150,
        helpUrl: '/docs/',
        homeRoute: '',
        logoOptions: {
          customLogoSrc: computed(() => getLogoUri() ?? ''),
        },
        saveLayoutOnRouteChange: true,
        // responsive: true,
      },
      notification: {
        component: markRaw(NotificationButton) as never,
      },
      session: {
        currentUser: computed(() => {
          return currentUser.value && {
            isCurrent: true,
            userId: currentUser.value.id,
            userName: currentUser.value.email,
          }
        }),
        onLogout: logout,
        onViewProfile: () => {
          if (!currentUser.value) return
          openEditMemberSidebar({ member: currentUser.value as Member })
        },
      },
      warnAutomation: false,
    })

    initializeNotifications({
      actionHandler: notificationActionsHandler,
      maxToasts: 3,
      showPopupNotifications: showPopupNotifications == null
        ? true
        : !!showPopupNotifications,
      timeout: 10000,
    })

    initializeParams({
      extraComponents: [
        DaemonParam,
      ],
    })

    // TODO these are mostly needed just to setup
    // i18n, which is a bit weird. should be investigated
    initializeDaemonParam()

    void loadUnfinishedExports()
    void loadUnfinishedImports()

    $q.iconMapFn = iconMapFn

    sessionStore.onLogin(() => {
      document.addEventListener('keydown', helpListener)

      if (sessionStore.currentUser?.tosAcceptanceTimestamp && shouldDisplayLicenseDialog()) {
        void Dialog.create({ component: LicenseExpiredDialog })
      }

      // TODO this is not best place
      void loadProjects()
    })

    sessionStore.onLogout(() => {
      document.removeEventListener('keydown', helpListener)
    })

    watch(isDark, (value) => {
      setCustomPrimaryColor(getPrimaryColor(), value)
    }, { immediate: true })
  },
})
</script>
